// src/pages/Instrument_Runs/components/InstrumentRunHeader/InstrumentRunHeader.tsx
import { useDateFormatter } from '@/hooks';
import type { InstrumentRunAssayVersion } from '@/types';
import { INSTRUMENT_RUN_ASSAY_VERSIONS } from '@/types';
import { isSixDigitCode } from '@/types/utils/sixDigitCode';
import { Code } from '@components/index';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Select } from '@nautilusbio/nb-design-system/Select';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../../styles.module.css';
import type { InstrumentRunForm, InstrumentRunPublic, SixDigitCode } from '../../types';
import RunStatusGrid from '../RunStatusGrid';

interface Props {
  run: InstrumentRunPublic;
  isEditing?: boolean;
  form?: InstrumentRunForm;
}

const hoverOpts = { delay: 2000 };

// Static options — defined at module level to avoid recreation on every render
const NO_OF_SAMPLES_OPTIONS = ['4', '12', '24', '36'] as const;
const SPECIES_OPTIONS = ['Human', 'Mouse'] as const;

type MetaFieldProps = { label: string; value: string };

const MetaField = ({ label, value }: MetaFieldProps) => (
  <Stack gap={1} orientation="vertical">
    <Text as="span" className={styles.headerMetaLabel} size="xs">
      {label}
    </Text>

    <Text size="sm">{value}</Text>
  </Stack>
);

const InstrumentRunHeader = ({ run, isEditing = false, form }: Props) => {
  const { t } = useTranslation('runs', { keyPrefix: 'header' });
  const formatDate = useDateFormatter();

  const showPin = run.status === 'SUBMITTED' && isSixDigitCode(run?.code ?? '');
  const Field = form?.Field;
  const canEdit = isEditing && Field;

  return (
    <Stack className={styles.headerCard} fullWidth orientation="vertical">
      {/* Title row: run name + assay version */}
      <Grid alignItems="center" className={styles.headerTitleRow} fullWidth gap={6} gridTemplateColumns="1fr .2fr">
        {canEdit ? (
          <Field name="name">
            {field => (
              <Input
                data-testid="run-name-input"
                fullWidth
                label={{
                  children: t('name'),
                  htmlFor: 'run-name',
                  id: 'label-run-name',
                }}
                onChange={e => field.handleChange((e.target as HTMLInputElement).value)}
                value={(field.state.value as string) ?? ''}
              />
            )}
          </Field>
        ) : (
          <Tooltip
            content={t('id_label', { id: run.id })}
            hoverOptions={hoverOpts}
            placement="right-start"
            trigger={<Text as="h1">{run.name}</Text>}
          />
        )}

        {canEdit ? (
          <Field name="assay_version">
            {field => (
              <Select
                allowClear={false}
                classNames={{
                  container: styles.assaySelect,
                }}
                data-testid="select-assay-version"
                id="assay-version"
                label={{
                  children: t('assay_version'),
                  id: 'label-assay-version',
                }}
                onChange={idx => {
                  const val = INSTRUMENT_RUN_ASSAY_VERSIONS[idx];
                  if (val !== undefined) field.handleChange(val);
                }}
                options={[...INSTRUMENT_RUN_ASSAY_VERSIONS]}
                value={INSTRUMENT_RUN_ASSAY_VERSIONS.indexOf(field.state.value as InstrumentRunAssayVersion)}
              />
            )}
          </Field>
        ) : (
          run.assay_version && (
            <Text className={styles.assaySelect} size="lg">
              {String(run.assay_version)}
            </Text>
          )
        )}
      </Grid>

      {/* Body row: description (left) + status grid (right) */}
      <Grid alignItems="start" className={styles.headerBodyRow} gap={6} gridTemplateColumns="3fr 1fr">
        {canEdit ? (
          <Field name="description">
            {field => (
              <Textarea
                data-testid="run-description-textarea"
                label={{
                  children: t('description'),
                  id: 'label-run-description',
                  htmlFor: 'run-description',
                }}
                onChange={e => field.handleChange((e.target as HTMLTextAreaElement).value)}
                value={(field.state.value as string) ?? ''}
              />
            )}
          </Field>
        ) : (
          run.description && (
            <Text as="small" className={styles.headerDescription} size="sm">
              {run.description}
            </Text>
          )
        )}

        <RunStatusGrid run={run} />
      </Grid>

      {/* Metadata grid: 3 cols × 3 rows */}
      <Grid className={styles.headerMetaGrid} gridTemplateColumns="repeat(3, 1fr)">
        {/* Row 1 */}
        {canEdit ? (
          <Field name="noOfSamples">
            {field => (
              <Select
                allowClear={false}
                data-testid="select-no-of-samples"
                id="no-of-samples"
                label={{ children: t('samples'), id: 'label-no-of-samples' }}
                onChange={idx => {
                  const val = NO_OF_SAMPLES_OPTIONS[idx];
                  if (val !== undefined) field.handleChange(val);
                }}
                options={[...NO_OF_SAMPLES_OPTIONS]}
                value={NO_OF_SAMPLES_OPTIONS.indexOf(
                  (field.state.value as (typeof NO_OF_SAMPLES_OPTIONS)[number]) ?? '4',
                )}
              />
            )}
          </Field>
        ) : (
          <MetaField label={t('samples')} value={typeof run.num_samples === 'number' ? String(run.num_samples) : '—'} />
        )}
        {canEdit ? (
          <Field name="species">
            {field => (
              <Select
                allowClear={false}
                data-testid="select-species"
                id="species"
                label={{ children: t('species'), id: 'label-species' }}
                onChange={idx => {
                  const val = SPECIES_OPTIONS[idx];
                  if (val !== undefined) field.handleChange(val);
                }}
                options={[...SPECIES_OPTIONS]}
                value={SPECIES_OPTIONS.indexOf(field.state.value as (typeof SPECIES_OPTIONS)[number])}
              />
            )}
          </Field>
        ) : (
          <MetaField label={t('species')} value={run.species ?? '—'} />
        )}
        {showPin ? (
          <Stack alignItems="center" gap={2}>
            <Text as="span" className={styles.headerMetaLabel}>
              {t('pin_to_start_run')}:
            </Text>

            <Code code={run.code as SixDigitCode} id={`${run.id}-code`} />
          </Stack>
        ) : (
          <MetaField label={t('instrument')} value={run.instrument_name ?? '—'} />
        )}

        {/* Row 2 */}
        <MetaField label={t('modified')} value={run.updated_at ? formatDate(run.updated_at) : '—'} />
        <MetaField label={t('started')} value={run.started_at ? formatDate(run.started_at) : '—'} />
        <MetaField label={t('submitted_by')} value={run.submitted_by_full_name ?? '—'} />

        {/* Row 3 */}
        <MetaField label={t('created')} value={formatDate(run.created_at)} />
        <MetaField label={t('finished')} value={run.completed_at ? formatDate(run.completed_at) : '—'} />
        <MetaField label={t('submitted')} value={run.submitted_at ? formatDate(run.submitted_at) : '—'} />
      </Grid>
    </Stack>
  );
};

export default memo(InstrumentRunHeader);
